<script setup>
import { computed, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import AiAPI from 'custom/api/ai';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import { useAiSettings } from './useAiSettings';
import { useConversationAi } from './useConversationAi';

// Above the reply box, after the inbox's assistant stepped aside: why (it handed off, or a person took over) and a
// button to let it answer again. While it answers, CE's own banner shows ("handled by ...", Take over). The status
// follows the conversation in the store (realtime); the assistant comes from the server.
const { t } = useLocoI18n();
const currentChat = useMapGetter('getSelectedChat');
const { isConfigured, load } = useAiSettings();
const { infoFor, refresh } = useConversationAi();

const isBusy = ref(false);

const conversationId = computed(() => currentChat.value?.id);
const assistant = computed(() => infoFor(conversationId.value)?.assistant);
const aiState = computed(
  () => currentChat.value?.additional_attributes?.loco_ai || null
);
// Only in open conversations the assistant took part in
const status = computed(() => {
  if (!assistant.value || currentChat.value?.status !== 'open') return null;
  if (!aiState.value) return null;
  return aiState.value.status === 'handed_off' ? 'handed_off' : 'paused';
});

const fetchAssistant = async () => {
  const id = conversationId.value;
  if (!id) return;
  await load();
  if (isConfigured.value) refresh(id);
};

const handBack = async () => {
  isBusy.value = true;
  try {
    await AiAPI.handBack(conversationId.value);
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isBusy.value = false;
  }
};

watch(conversationId, fetchAssistant, { immediate: true });
</script>

<template>
  <div
    v-if="status"
    class="flex flex-wrap items-center gap-2 mx-4 mb-2 px-3 py-2 rounded-lg text-sm bg-n-alpha-1 text-n-slate-11"
  >
    <Icon icon="i-lucide-bot-off" class="size-4 flex-shrink-0" />
    <span class="flex-1 min-w-0">
      {{
        status === 'handed_off'
          ? t('LOCO.AI.BAR.HANDED_OFF', {
              name: assistant.name,
              reason: aiState.reason || '',
            })
          : t('LOCO.AI.BAR.PAUSED', { name: assistant.name })
      }}
    </span>
    <Button
      xs
      faded
      slate
      icon="i-lucide-sparkles"
      :label="t('LOCO.AI.BAR.HAND_BACK', { name: assistant.name })"
      :is-loading="isBusy"
      @click="handBack"
    />
  </div>
</template>
